/**
 * AssetPreview — renders an inline preview for video, audio, image, PDF, and text files.
 *
 * Fetches a presigned URL via the existing download-url endpoint and renders
 * the appropriate HTML5 element based on MIME type. Audio files get a
 * canvas waveform with click-to-seek. PDF uses browser's native viewer.
 *
 * Robustness:
 * - Size guards prevent memory spikes (audio decode capped, text fetch capped)
 * - Fetch timeout (30s URL, 60s media) prevents hanging requests
 * - Canvas roundRect fallback for older browsers
 * - ResizeObserver redraws canvas on container resize
 * - AudioContext reused across decodes (browser limit: ~6 concurrent)
 * - Play/pause state driven by audio element events (single source of truth)
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { Film, Music, Image, FileText, FileQuestion, Maximize2, FileSpreadsheet, FileArchive, Play, Pause, AlertTriangle } from 'lucide-react'
import type { Asset } from '../../types/fileManager'
import { assetApi } from '../../services/assetFolderApi'

// ─── Constants ───────────────────────────────────────────────────────────────

type PreviewCategory = 'video' | 'audio' | 'image' | 'pdf' | 'text' | 'office' | 'archive' | 'unsupported'

const TEXT_MIME_PREFIXES = ['text/']
const TEXT_MIME_EXACT = [
  'application/json',
  'application/xml',
  'application/javascript',
  'application/x-javascript',
  'application/typescript',
  'application/x-yaml',
  'application/x-sh',
  'application/x-httpd-php',
  'application/sql',
  'application/graphql',
  'application/ld+json',
  'application/x-toml',
]
const OFFICE_MIME = [
  'application/msword',
  'application/vnd.ms-excel',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.oasis.opendocument.text',
  'application/vnd.oasis.opendocument.spreadsheet',
  'application/vnd.oasis.opendocument.presentation',
  'application/rtf',
]
const ARCHIVE_MIME = [
  'application/zip',
  'application/x-rar-compressed',
  'application/gzip',
  'application/x-tar',
  'application/x-7z-compressed',
  'application/x-bzip2',
]

const MAX_TEXT_SIZE = 1_000_000       // 1 MB — text beyond this is truncated
const MAX_AUDIO_DECODE_SIZE = 100_000_000  // 100 MB — skip waveform for larger files
const FETCH_TIMEOUT_MS = 30_000       // 30s for URL / text fetches
const MEDIA_FETCH_TIMEOUT_MS = 60_000 // 60s for audio waveform decode fetch

// ─── Helpers ─────────────────────────────────────────────────────────────────

const VIDEO_EXTS = new Set(['mp4','avi','mov','wmv','flv','webm','mkv','m4v','ts','mts','m2ts','mxf','r3d','braw','prores'])
const AUDIO_EXTS = new Set(['mp3','wav','ogg','flac','aac','m4a','aiff','aif','wma'])
const IMAGE_EXTS = new Set(['jpg','jpeg','png','gif','bmp','tiff','tif','webp','exr','dpx','psd'])

function getPreviewCategory(mimeType: string, filename?: string): PreviewCategory {
  if (!mimeType) return 'unsupported'
  if (mimeType.startsWith('video/')) return 'video'
  if (mimeType.startsWith('audio/')) return 'audio'
  if (mimeType.startsWith('image/')) return 'image'
  if (mimeType === 'application/pdf') return 'pdf'
  if (TEXT_MIME_PREFIXES.some(p => mimeType.startsWith(p))) return 'text'
  if (TEXT_MIME_EXACT.includes(mimeType)) return 'text'
  if (OFFICE_MIME.includes(mimeType)) return 'office'
  if (ARCHIVE_MIME.includes(mimeType)) return 'archive'
  // Fallback: check file extension for misreported MIME types
  if (filename) {
    const ext = filename.split('.').pop()?.toLowerCase()
    if (ext && VIDEO_EXTS.has(ext)) return 'video'
    if (ext && AUDIO_EXTS.has(ext)) return 'audio'
    if (ext && IMAGE_EXTS.has(ext)) return 'image'
  }
  return 'unsupported'
}

function getOfficeLabel(mimeType: string): string {
  if (mimeType.includes('word') || mimeType.includes('document')) return 'Word Document'
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'Spreadsheet'
  if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return 'Presentation'
  if (mimeType === 'application/rtf') return 'Rich Text'
  return 'Office Document'
}

function getFallbackIcon(mimeType: string) {
  if (mimeType?.startsWith('video/')) return <Film className="w-8 h-8 text-[var(--rq-blue)]" />
  if (mimeType?.startsWith('audio/')) return <Music className="w-8 h-8 text-[var(--rq-amber)]" />
  if (mimeType?.startsWith('image/')) return <Image className="w-8 h-8 text-emerald-400" />
  if (mimeType?.startsWith('text/') || TEXT_MIME_EXACT.includes(mimeType)) return <FileText className="w-8 h-8 text-amber-400" />
  if (OFFICE_MIME.includes(mimeType)) return <FileSpreadsheet className="w-8 h-8 text-blue-400" />
  if (ARCHIVE_MIME.includes(mimeType)) return <FileArchive className="w-8 h-8 text-purple-400" />
  return <FileQuestion className="w-8 h-8 text-[var(--rq-text-soft)]" />
}

function formatAudioTime(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

/** Create an AbortController that auto-aborts after `ms` milliseconds. */
function timedAbort(ms: number, existingSignal?: AbortSignal): { controller: AbortController; clear: () => void } {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(new DOMException('Timeout', 'TimeoutError')), ms)

  // If the caller already has a signal (e.g. from cleanup), chain it
  if (existingSignal) {
    existingSignal.addEventListener('abort', () => controller.abort(existingSignal.reason), { once: true })
  }

  return { controller, clear: () => clearTimeout(timer) }
}

/** Canvas roundRect polyfill — older Safari/Firefox lack roundRect. */
function drawRoundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  if (typeof ctx.roundRect === 'function') {
    ctx.roundRect(x, y, w, h, r)
  } else {
    // Fallback: simple rect (radius is cosmetic at 1-2px, not worth a full arc polyfill)
    ctx.rect(x, y, w, h)
  }
}

// ─── Shared AudioContext ─────────────────────────────────────────────────────
// Browsers limit concurrent AudioContexts (~6). Reuse a single one.

let sharedAudioCtx: AudioContext | null = null

function getAudioContext(): AudioContext {
  if (!sharedAudioCtx || sharedAudioCtx.state === 'closed') {
    sharedAudioCtx = new AudioContext()
  }
  return sharedAudioCtx
}

// ─── Audio Waveform ──────────────────────────────────────────────────────────

interface AudioWaveformProps {
  url: string
  compact: boolean
  fileSize: number
  expandButton: React.ReactNode
}

function AudioWaveform({ url, compact, fileSize, expandButton }: AudioWaveformProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const audioRef = useRef<HTMLAudioElement>(null)
  const animFrameRef = useRef<number>(0)
  const [peaks, setPeaks] = useState<number[] | null>(null)
  const [playing, setPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [waveError, setWaveError] = useState(false)
  const [tooLarge, setTooLarge] = useState(false)

  // Check size before attempting waveform decode
  useEffect(() => {
    if (fileSize > MAX_AUDIO_DECODE_SIZE) {
      setTooLarge(true)
      return
    }
    setTooLarge(false)
  }, [fileSize])

  // Decode audio and extract peaks (skip if too large)
  useEffect(() => {
    if (tooLarge) return

    let cancelled = false
    const { controller, clear } = timedAbort(MEDIA_FETCH_TIMEOUT_MS)

    fetch(url, { signal: controller.signal })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        // Double-check Content-Length if server sends it (file_size may be stale)
        const cl = res.headers.get('content-length')
        if (cl && parseInt(cl, 10) > MAX_AUDIO_DECODE_SIZE) {
          setTooLarge(true)
          return undefined
        }
        return res.arrayBuffer()
      })
      .then(buffer => {
        if (!buffer || cancelled) return
        const ctx = getAudioContext()
        // decodeAudioData copies the buffer — the ArrayBuffer is detached after
        return ctx.decodeAudioData(buffer).then(audioBuffer => {
          if (cancelled) return
          const channelData = audioBuffer.getChannelData(0)
          const numBars = compact ? 80 : 200
          const blockSize = Math.floor(channelData.length / numBars)
          if (blockSize === 0) { setWaveError(true); return }
          const extracted: number[] = []
          for (let i = 0; i < numBars; i++) {
            let sum = 0
            const start = i * blockSize
            for (let j = 0; j < blockSize; j++) {
              sum += Math.abs(channelData[start + j])
            }
            extracted.push(sum / blockSize)
          }
          const max = Math.max(...extracted, 0.01)
          setPeaks(extracted.map(v => v / max))
          setDuration(audioBuffer.duration)
        })
      })
      .catch(err => {
        if (cancelled) return
        if (err?.name === 'AbortError' || err?.name === 'TimeoutError') {
          // Timeout or user-navigated-away — fall back to basic player
          setWaveError(true)
        } else {
          setWaveError(true)
        }
      })
      .finally(clear)

    return () => { cancelled = true; controller.abort() }
  }, [url, compact, tooLarge])

  // Draw waveform
  const drawWaveform = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas || !peaks) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const dpr = window.devicePixelRatio || 1
    const rect = canvas.getBoundingClientRect()
    canvas.width = rect.width * dpr
    canvas.height = rect.height * dpr
    ctx.scale(dpr, dpr)

    const w = rect.width
    const h = rect.height
    const barW = Math.max(1, (w / peaks.length) - 1)
    const gap = 1
    const progress = duration > 0 ? currentTime / duration : 0

    ctx.clearRect(0, 0, w, h)

    for (let i = 0; i < peaks.length; i++) {
      const x = i * (barW + gap)
      const barH = Math.max(2, peaks[i] * h * 0.85)
      const y = (h - barH) / 2
      const barProgress = i / peaks.length

      ctx.fillStyle = barProgress <= progress
        ? 'rgba(245, 158, 11, 0.9)' // amber played
        : 'rgba(255, 255, 255, 0.2)' // unplayed

      ctx.beginPath()
      drawRoundRect(ctx, x, y, barW, barH, 1)
      ctx.fill()
    }
  }, [peaks, currentTime, duration])

  useEffect(() => { drawWaveform() }, [drawWaveform])

  // Redraw on container resize
  useEffect(() => {
    const container = containerRef.current
    if (!container || !peaks) return
    const observer = new ResizeObserver(() => drawWaveform())
    observer.observe(container)
    return () => observer.disconnect()
  }, [peaks, drawWaveform])

  // Animation loop for playback position
  useEffect(() => {
    if (!playing) return
    const tick = () => {
      if (audioRef.current) {
        setCurrentTime(audioRef.current.currentTime)
      }
      animFrameRef.current = requestAnimationFrame(tick)
    }
    animFrameRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(animFrameRef.current)
  }, [playing])

  // Click to seek
  const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    const audio = audioRef.current
    if (!canvas || !audio || !duration) return
    const rect = canvas.getBoundingClientRect()
    const x = e.clientX - rect.left
    const ratio = Math.max(0, Math.min(1, x / rect.width))
    audio.currentTime = ratio * duration
    setCurrentTime(audio.currentTime)
  }

  const togglePlay = () => {
    const audio = audioRef.current
    if (!audio) return
    if (playing) {
      audio.pause()
    } else {
      audio.play().catch(() => {
        // Autoplay blocked or source error — stay paused
        setPlaying(false)
      })
    }
  }

  // Basic audio fallback (too large for waveform, or decode failed)
  if (tooLarge || waveError) {
    return (
      <div className={`relative flex flex-col items-center justify-center gap-3 bg-black/20 rounded-lg ${compact ? 'h-44 px-4' : 'flex-1 min-h-[200px] px-8'}`}>
        {expandButton}
        <div className="w-12 h-12 flex items-center justify-center bg-[var(--rq-amber)]/10 rounded-full">
          <Music className="w-6 h-6 text-[var(--rq-amber)]" />
        </div>
        {tooLarge && (
          <p className="text-[10px] text-[var(--rq-text-soft)]">File too large for waveform preview</p>
        )}
        <audio key={url} src={url} controls preload="metadata" className="w-full max-w-sm" onError={() => {}} />
      </div>
    )
  }

  return (
    <div ref={containerRef} className={`relative flex flex-col bg-black/20 rounded-lg overflow-hidden ${compact ? 'h-44' : 'flex-1 min-h-[200px]'}`}>
      {expandButton}
      <audio
        ref={audioRef}
        src={url}
        preload="auto"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setCurrentTime(0) }}
        onTimeUpdate={() => {
          // Coarse sync (animation loop handles fine-grained updates during playback)
          if (!playing && audioRef.current) setCurrentTime(audioRef.current.currentTime)
        }}
        onLoadedMetadata={() => { if (audioRef.current) setDuration(audioRef.current.duration) }}
        onError={() => setWaveError(true)}
      />

      {/* Waveform */}
      <div className={`flex-1 flex items-center px-3 ${compact ? 'py-2' : 'px-6 py-4'}`}>
        {peaks ? (
          <canvas
            ref={canvasRef}
            className="w-full h-full cursor-pointer"
            onClick={handleCanvasClick}
          />
        ) : (
          <div className="w-full flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-white/20 border-t-[var(--rq-amber)] rounded-full animate-spin" />
          </div>
        )}
      </div>

      {/* Controls bar */}
      <div className="flex items-center gap-3 px-3 py-2 border-t border-white/10 bg-black/20">
        <button
          onClick={togglePlay}
          disabled={!peaks}
          className="w-8 h-8 flex items-center justify-center rounded-full bg-[var(--rq-amber)] hover:bg-[var(--rq-amber)]/80 text-black disabled:opacity-30 transition-colors"
        >
          {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
        </button>
        <span className="text-xs text-[var(--rq-text-soft)] tabular-nums">
          {formatAudioTime(currentTime)}
        </span>
        <div className="flex-1" />
        <span className="text-xs text-[var(--rq-text-soft)] tabular-nums">
          {duration > 0 ? formatAudioTime(duration) : '--:--'}
        </span>
      </div>
    </div>
  )
}

// ─── Main Preview ────────────────────────────────────────────────────────────

interface AssetPreviewProps {
  asset: Asset
  compact?: boolean
  onExpand?: () => void
}

export default function AssetPreview({ asset, compact = true, onExpand }: AssetPreviewProps) {
  const [url, setUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [textContent, setTextContent] = useState<string | null>(null)
  const [textTruncated, setTextTruncated] = useState(false)

  const category = getPreviewCategory(asset.file_type, asset.original_filename)

  useEffect(() => {
    let cancelled = false
    const { controller, clear } = timedAbort(FETCH_TIMEOUT_MS)

    setLoading(true)
    setError(null)
    setUrl(null)
    setTextContent(null)
    setTextTruncated(false)

    // Categories that don't need a URL
    if (category === 'unsupported' || category === 'office' || category === 'archive') {
      setLoading(false)
      clear()
      return
    }

    const loadUrl = category === 'video'
      ? assetApi.getPreviewUrl(asset.id)
      : assetApi.getDownloadUrl(asset.id)

    loadUrl
      .then(async (res) => {
        if (cancelled) return
        setUrl(res.url)

        // For text files, fetch the content inline
        if (category === 'text') {
          if (asset.file_size > MAX_TEXT_SIZE) {
            // Still fetch first chunk rather than refusing entirely
            try {
              const resp = await fetch(res.url, {
                signal: controller.signal,
                headers: { Range: `bytes=0-${MAX_TEXT_SIZE - 1}` },
              })
              if (cancelled) return
              if (!resp.ok && resp.status !== 206) throw new Error(`HTTP ${resp.status}`)
              const text = await resp.text()
              if (cancelled) return
              setTextContent(text)
              setTextTruncated(true)
            } catch (err) {
              if (cancelled) return
              const name = (err as { name?: unknown })?.name
              if (name === 'AbortError' || name === 'TimeoutError') {
                setError('Request timed out')
              } else {
                setError('Failed to load text')
              }
            }
            if (!cancelled) setLoading(false)
            return
          }

          try {
            const resp = await fetch(res.url, { signal: controller.signal })
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
            const text = await resp.text()
            if (cancelled) return
            if (text.length > MAX_TEXT_SIZE) {
              setTextContent(text.slice(0, MAX_TEXT_SIZE))
              setTextTruncated(true)
            } else {
              setTextContent(text)
            }
          } catch (err) {
            if (cancelled) return
            const name = (err as { name?: unknown })?.name
            if (name === 'AbortError' || name === 'TimeoutError') {
              setError('Request timed out')
            } else {
              setError('Failed to load text')
            }
          }
        }

        if (!cancelled) setLoading(false)
      })
      .catch((err) => {
        if (!cancelled) {
          if (err?.name === 'TimeoutError' || err?.message?.includes('Timeout')) {
            setError('Request timed out')
          } else {
            setError('Failed to load preview')
          }
          setLoading(false)
        }
      })
      .finally(clear)

    return () => { cancelled = true; controller.abort() }
  }, [asset.id, asset.file_type, asset.file_size, category])

  // Loading skeleton
  if (loading) {
    return (
      <div className={`flex items-center justify-center bg-black/20 rounded-lg ${compact ? 'h-44' : 'flex-1 min-h-[300px]'}`}>
        <div className="w-8 h-8 border-2 border-white/20 border-t-[var(--rq-blue)] rounded-full animate-spin" />
      </div>
    )
  }

  // Error fallback — shows specific error message
  if (error) {
    return (
      <div className={`flex flex-col items-center justify-center gap-2 bg-black/20 rounded-lg ${compact ? 'h-44' : 'flex-1 min-h-[300px]'}`}>
        <div className="w-16 h-16 flex items-center justify-center bg-white/5 rounded-xl">
          {error.includes('timed out') ? (
            <AlertTriangle className="w-8 h-8 text-amber-400" />
          ) : (
            getFallbackIcon(asset.file_type)
          )}
        </div>
        <p className="text-xs text-[var(--rq-text-soft)]">{error}</p>
      </div>
    )
  }

  // Office document — styled card with type label
  if (category === 'office') {
    return (
      <div className={`relative flex flex-col items-center justify-center gap-3 bg-black/20 rounded-lg ${compact ? 'h-44' : 'flex-1 min-h-[300px]'}`}>
        {onExpand && (
          <button onClick={onExpand} className="absolute top-2 right-2 p-1.5 rounded-lg bg-black/60 hover:bg-black/80 text-white/70 hover:text-white transition-colors z-10" title="Fullscreen preview">
            <Maximize2 className="w-4 h-4" />
          </button>
        )}
        <div className="w-14 h-14 flex items-center justify-center bg-blue-500/10 rounded-xl">
          <FileSpreadsheet className="w-7 h-7 text-blue-400" />
        </div>
        <p className="text-sm font-medium text-[var(--rq-text)]">{getOfficeLabel(asset.file_type)}</p>
        <p className="text-xs text-[var(--rq-text-soft)]">Download to view</p>
      </div>
    )
  }

  // Archive — styled card
  if (category === 'archive') {
    return (
      <div className={`relative flex flex-col items-center justify-center gap-3 bg-black/20 rounded-lg ${compact ? 'h-44' : 'flex-1 min-h-[300px]'}`}>
        <div className="w-14 h-14 flex items-center justify-center bg-purple-500/10 rounded-xl">
          <FileArchive className="w-7 h-7 text-purple-400" />
        </div>
        <p className="text-sm font-medium text-[var(--rq-text)]">Archive</p>
        <p className="text-xs text-[var(--rq-text-soft)]">Download to extract</p>
      </div>
    )
  }

  // Unsupported
  if (category === 'unsupported') {
    return (
      <div className={`flex flex-col items-center justify-center gap-2 bg-black/20 rounded-lg ${compact ? 'h-44' : 'flex-1 min-h-[300px]'}`}>
        <div className="w-16 h-16 flex items-center justify-center bg-white/5 rounded-xl">
          {getFallbackIcon(asset.file_type)}
        </div>
        <p className="text-xs text-[var(--rq-text-soft)]">No preview for this file type</p>
      </div>
    )
  }

  const expandButton = onExpand ? (
    <button
      onClick={onExpand}
      className="absolute top-2 right-2 p-1.5 rounded-lg bg-black/60 hover:bg-black/80 text-white/70 hover:text-white transition-colors z-10"
      title="Fullscreen preview"
    >
      <Maximize2 className="w-4 h-4" />
    </button>
  ) : null

  // Video
  if (category === 'video' && url) {
    return (
      <div className={`relative bg-black rounded-lg overflow-hidden ${compact ? 'h-44' : 'flex-1 flex items-center justify-center min-h-[300px]'}`}>
        {expandButton}
        <video
          key={url}
          src={url}
          controls
          preload="metadata"
          className={compact ? 'w-full h-full object-contain' : 'max-w-full max-h-full'}
          onError={() => setError('Video failed to load')}
        />
      </div>
    )
  }

  // Audio — waveform with seek
  if (category === 'audio' && url) {
    return <AudioWaveform url={url} compact={compact} fileSize={asset.file_size} expandButton={expandButton} />
  }

  // Image
  if (category === 'image' && url) {
    return (
      <div className={`relative bg-black/20 rounded-lg overflow-hidden flex items-center justify-center ${compact ? 'h-44' : 'flex-1 min-h-[300px]'}`}>
        {expandButton}
        <img
          src={url}
          alt={asset.original_filename}
          className="max-w-full max-h-full object-contain"
          onError={() => setError('Image failed to load')}
        />
      </div>
    )
  }

  // PDF — browser's native viewer
  // sandbox="allow-same-origin allow-scripts" is needed for Firefox's pdf.js
  if (category === 'pdf' && url) {
    return (
      <div className={`relative bg-[#1a1a2e] rounded-lg overflow-hidden flex flex-col ${compact ? 'h-44' : 'flex-1 min-h-[400px]'}`}>
        {expandButton}
        <iframe
          src={url}
          title={asset.original_filename}
          className="w-full flex-1 border-0 rounded-lg"
          sandbox="allow-same-origin allow-scripts"
        />
      </div>
    )
  }

  // Text / Code
  if (category === 'text' && textContent !== null) {
    return (
      <div className={`relative bg-black/30 rounded-lg overflow-hidden ${compact ? 'h-44' : 'flex-1 min-h-[300px]'}`}>
        {expandButton}
        <pre className="w-full h-full overflow-auto p-3 text-xs text-[var(--rq-text)] font-mono leading-relaxed whitespace-pre-wrap break-words">
          {textContent}
        </pre>
        {textTruncated && (
          <div className="absolute bottom-0 left-0 right-0 px-3 py-1.5 bg-gradient-to-t from-black/80 to-transparent text-[10px] text-[var(--rq-text-soft)] text-center">
            File truncated — download for full content
          </div>
        )}
      </div>
    )
  }

  return null
}
